import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { Subject, interval, of } from 'rxjs';
import { switchMap, catchError, takeUntil, filter } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { EditingFlowService } from './editing-flow.service';

interface VersionResponse {
    version: number;
}

@Injectable({
    providedIn: 'root',
})
export class VersionCheckService implements OnDestroy {
    private currentVersion: number | null = null;
    private destroy$ = new Subject<void>();
    /** True once the toast has actually rendered — stops it re-firing on later polls. */
    private updatePromptShown = false;
    /** True when a new version was detected but the toast is held back behind an editing flow. */
    private updateDeferred = false;

    // Check every 60 seconds (configurable)
    private readonly CHECK_INTERVAL_MS = 60000;

    constructor(
        private http: HttpClient,
        private toastr: ToastrService,
        private editingFlow: EditingFlowService,
    ) {}

    /**
     * Initialize version checking. Call this once from AppComponent.
     * Only runs in non-local environments where version.json exists.
     * Pass forceEnable=true to test locally (requires manually creating version.json).
     */
    initVersionCheck(forceEnable = false): void {
        // Expose simulate function for testing (not in production)
        if (!environment.production) {
            (window as any).versionCheck = {
                currentVersion: () => this.currentVersion,
                // Goes through the real deferral path, so opening a drawer/dialog
                // first is enough to test suppression.
                simulateUpdate: () => this.requestPrompt(),
                // Bypasses deferral — shows the toast immediately.
                forceToast: () => this.promptReload(),
            };
        }

        // Skip version checking in local development unless forced
        if (environment.local && !forceEnable) {
            console.log('[VersionCheck] Skipped - local environment');
            console.log(
                '[VersionCheck] To test notification: window.versionCheck.simulateUpdate()',
            );
            return;
        }

        console.log('[VersionCheck] Initializing...');

        // Load initial version
        this.fetchVersion().subscribe((version) => {
            if (version !== null) {
                this.currentVersion = version;
                console.log('[VersionCheck] Initial version:', version);
                console.log(
                    '[VersionCheck] To test: window.versionCheck.simulateUpdate()',
                );
                this.startPolling();
            } else {
                console.log('[VersionCheck] No version.json found');
            }
        });
    }

    private startPolling(): void {
        interval(this.CHECK_INTERVAL_MS)
            .pipe(
                takeUntil(this.destroy$),
                switchMap(() => this.fetchVersion()),
                filter((version) => version !== null),
            )
            .subscribe((version) => {
                if (
                    this.currentVersion !== null &&
                    version !== this.currentVersion &&
                    !this.updatePromptShown &&
                    !this.updateDeferred
                ) {
                    this.requestPrompt();
                }
            });
    }

    /**
     * Show the toast, unless the user is mid-edit in the side drawer or a dialog.
     * The toast is bottom-right and overlaps the drawer's Cancel/Update buttons,
     * so firing it during an editing flow leaves the user unable to finish it.
     */
    private requestPrompt(): void {
        if (!this.editingFlow.isOpen()) {
            this.promptReload();
            return;
        }

        this.updateDeferred = true;
        this.editingFlow
            .whenClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.updateDeferred = false;
                this.promptReload();
            });
    }

    private fetchVersion() {
        return this.http
            .get<VersionResponse>('/assets/version.json', {
                headers: { 'Cache-Control': 'no-cache' },
            })
            .pipe(
                switchMap((response) => of(response.version)),
                catchError(() => {
                    // version.json may not exist in some environments
                    return of(null);
                }),
            );
    }

    private promptReload(): void {
        this.updatePromptShown = true;

        const toast = this.toastr.show(
            `
            <div class="version-toast-content">
                <div class="version-toast-message">A new version of DoorSpot is available.</div>
                <div class="version-toast-actions">
                    <a href="https://changelog.doorspot.com" target="_blank" class="version-toast-link">See what's new</a>
                    <span class="version-toast-divider">|</span>
                    <a href="javascript:void(0)" class="version-toast-refresh">Refresh now</a>
                </div>
            </div>
            `,
            '',
            {
                disableTimeOut: true,
                closeButton: true,
                tapToDismiss: false,
                positionClass: 'toast-bottom-right',
                enableHtml: true,
                toastClass: 'ngx-toastr version-update-toast',
            },
        );

        // Handle refresh click via event delegation
        toast.onShown.subscribe(() => {
            const toastEl = document.querySelector('.version-update-toast');
            toastEl?.addEventListener('click', (e) => {
                const target = e.target as HTMLElement;
                if (target.classList.contains('version-toast-refresh')) {
                    e.preventDefault();
                    window.location.reload();
                }
            });
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
